import { safeParseDateInput } from '../../../helpers/date-time-helpers/parsing/parsing';
import { PHASES } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.constants';
import { isFutureDate } from '../dashboards.helpers';
import type { SprintData } from '../hooks/dashboards.hooks.type';

type CumulativeFlowMetric = 'tasks' | 'points';

/**
 * A single rendered cumulative-flow point: a date plus one stacked value per
 * phase. Future days carry `undefined` phase values so the stacked areas gap.
 */
interface CumulativeFlowDatum {
  date: string;
  [phaseId: string]: string | number | undefined;
}

/**
 * Builds the per-day stacked-area series for a cumulative-flow sprint widget.
 *
 * Responsibilities:
 * 1. Resolve each phase's value for the selected metric (tasks or points).
 * 2. Leave every phase `undefined` for not-yet-elapsed (future) days so the
 *    stacked areas render a gap instead of a misleading 0, while keeping the
 *    date so its x-axis tick is preserved.
 * 3. Sort chronologically so the rendered order is stable.
 *
 * @param {SprintData | undefined} data - The selected sprint's chart data.
 * @param {CumulativeFlowMetric} selectedMetric - Whether to read tasks or points.
 * @returns {CumulativeFlowDatum[]} Chronologically sorted points ready for Recharts.
 */
const buildCumulativeFlowChartData = (
  data: SprintData | undefined,
  selectedMetric: CumulativeFlowMetric,
): CumulativeFlowDatum[] => {
  if (!data || !data.data || data.data.length === 0) {
    return [];
  }

  const rawData = data.data as Array<Record<string, unknown>>;
  return rawData
    .map((dataPoint) => {
      const date = dataPoint.date as string;
      const result: CumulativeFlowDatum = { date };
      // Not-yet-elapsed days come back as zeros for an in-progress sprint. Keep
      // the date on the x-axis but leave the phases undefined so the stacked
      // areas (connectNulls={false}) gap rather than dropping to 0.
      const future = isFutureDate(date);
      PHASES.forEach((phaseId) => {
        if (future) {
          result[phaseId] = undefined;
          return;
        }
        const taskKey = `tasks_${phaseId}`;
        const pointKey = `points_${phaseId}`;
        result[phaseId] =
          selectedMetric === 'tasks'
            ? ((dataPoint[taskKey] ?? dataPoint[phaseId] ?? 0) as number)
            : ((dataPoint[pointKey] ?? dataPoint[phaseId] ?? 0) as number);
      });
      return result;
    })
    .sort((a, b) => {
      const dateA = safeParseDateInput(a.date);
      const dateB = safeParseDateInput(b.date);
      if (!dateA || !dateB) {
        return 0;
      }
      return dateA.valueOf() - dateB.valueOf();
    });
};

export { buildCumulativeFlowChartData };
